import {
  OuterCavityLeafTypes,
  WallInsulationTypes,
  WallTypeTypes,
} from "@iko-design-center/shared"
import { useHistory } from "react-router"
import { useRoutes } from "../routes/hooks"
import { Models } from "../../components/viewer/renderer.api"
import { useApplication } from "../application/hooks"
import { useCavityWall } from "../cavity-wall/hooks"
import { CavityWallData } from "../cavity-wall/store"
import { checkValidQueriedValueOfType, getEnumKey } from "./query-helpers"
import { useTranslation } from "react-i18next"

export function useQueryParams() {
  const { setCurrentModel } = useApplication()
  const { t } = useTranslation() as any
  const { Routes } = useRoutes()
  const history = useHistory()

  const {
    setWallTypeType,
    setWallInsulationType,
    setOuterCavityLeafType,
    setWallTypeTypeWithThickness,
    setWallInsulationTypeWithThickness,
    setOuterCavityLeafTypeWithThickness,
  } = useCavityWall()

  const setQueryParams = (
    data: CavityWallData,
    language: string,
    redirect = false
  ) => {
    const languageRoute = t("routes.CAVITY_WALL_3_OVERVIEW", { lng: language })
    const urlWithoutParams = `${window.location.origin}${languageRoute}`
    const url = new URL(urlWithoutParams ?? window.location.href)

    // Wall type
    const wallType = data.wallType.type
    const wallTypeEnum = getEnumKey(wallType, WallTypeTypes)
    if (wallTypeEnum) {
      url.searchParams.append("wall-type", wallTypeEnum)

      // Include wall type thickness
      const wallTypeThermalThickness =
        data.thermalPerformance.wallType.thickness
      if (wallTypeThermalThickness) {
        url.searchParams.append(
          "wall-type-thickness",
          wallTypeThermalThickness.toString()
        )
      }
    }

    // Wall insulation
    const wallInsulation = data.wallInsulation.type
    const wallInsulationEnum = getEnumKey(wallInsulation, WallInsulationTypes)
    if (wallInsulationEnum) {
      url.searchParams.append("wall-insulation", wallInsulationEnum)

      // Include wall insulation thickness
      const wallInsulationThermalThickness =
        data.thermalPerformance.wallInsulation.thickness
      if (wallInsulationThermalThickness) {
        url.searchParams.append(
          "wall-insulation-thickness",
          wallInsulationThermalThickness.toString()
        )
      }
    }

    // Outer cavity leaf
    const outerCavityLeaf = data.outerCavityLeaf.type
    const outerCavityLeafEnum = getEnumKey(
      outerCavityLeaf,
      OuterCavityLeafTypes
    )
    if (outerCavityLeafEnum) {
      url.searchParams.append("outer-cavity-leaf", outerCavityLeafEnum)

      // Include outer cavity leaf thickness
      const outerCavityLeafThermalThickness =
        data.thermalPerformance.outerCavityLeaf.thickness
      if (outerCavityLeafThermalThickness) {
        url.searchParams.append(
          "outer-cavity-leaf-thickness",
          outerCavityLeafThermalThickness.toString()
        )
      }
    }

    if (redirect) {
      window.location.href = url.href
    } else {
      window.history.replaceState([], document.title, url.href as string)
    }
  }

  const redirectBasedOnQueryParams = () => {
    const url = new URL(window.location.href)
    const query = {
      wallType: url.searchParams.get("wall-type"),
      wallTypeThickness: url.searchParams.get("wall-type-thickness"),
      wallInsulation: url.searchParams.get("wall-insulation"),
      wallInsulationThickness: url.searchParams.get(
        "wall-insulation-thickness"
      ),
      outerCavityLeaf: url.searchParams.get("outer-cavity-leaf"),
      outerCavityLeafThickness: url.searchParams.get(
        "outer-cavity-leaf-thickness"
      ),
      detail: url.searchParams.get("detail"),
      token: url.searchParams.get("token"),
    }

    // Wall Type
    const validWallType = checkValidQueriedValueOfType(
      query.wallType,
      WallTypeTypes
    )
    if (validWallType) {
      // Include wall type thickness
      if (query.wallTypeThickness) {
        setWallTypeTypeWithThickness(validWallType, query.wallTypeThickness)
      } else {
        setWallTypeType(validWallType)
      }
    }

    // Wall Insulation
    const validWallInsulation = checkValidQueriedValueOfType(
      query.wallInsulation,
      WallInsulationTypes
    )
    if (validWallInsulation) {
      // Include wall insulation thickness
      if (query.wallInsulationThickness) {
        setWallInsulationTypeWithThickness(
          validWallInsulation,
          query.wallInsulationThickness
        )
      } else {
        setWallInsulationType(validWallInsulation)
      }
    }

    // Outer Cavity Leaf
    const validOuterCavityLeaf = checkValidQueriedValueOfType(
      query.outerCavityLeaf,
      OuterCavityLeafTypes
    )
    if (validOuterCavityLeaf) {
      // Include outer cavity leaf thickness
      if (query.outerCavityLeafThickness) {
        setOuterCavityLeafTypeWithThickness(
          validOuterCavityLeaf,
          query.outerCavityLeafThickness
        )
      } else {
        setOuterCavityLeafType(validOuterCavityLeaf)
      }
    }

    // Model
    const validModel = checkValidQueriedValueOfType(query.detail, Models)
    if (validModel) setCurrentModel(validModel)
    else setCurrentModel(Models.CAVITY_WALL)

    // Routing
    let route = Routes.CAVITY_WALL_1_WALL_TYPE
    if (validWallType && validWallInsulation && validOuterCavityLeaf) {
      route = Routes.CAVITY_WALL_3_OVERVIEW
    } else if (validWallType) {
      route = Routes.CAVITY_WALL_2_WALL_INSULATION
    }

    if (query.token) {
      history.push({
        pathname: route as any,
        search: `?token=${query.token}`,
      })
    } else {
      history.push(route as any)
    }
  }

  return { setQueryParams, redirectBasedOnQueryParams }
}
